import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatMinute } from '../../utils/matchClock';
import { matchMetaAPI } from '../../services/api';

const STATUS = {
    prepared:       { label: 'Vorbereitung', badge: 'ticker-badge--prep' },
    live:           { label: 'LIVE',         badge: 'ticker-badge--live' },
    halftime:       { label: 'Halbzeit',     badge: 'ticker-badge--ht' },
    finished:       { label: 'Abgepfiffen',  badge: 'ticker-badge--done' },
    report_pending: { label: 'Bericht',      badge: 'ticker-badge--prep' },
    published:      { label: 'Veröffentlicht', badge: 'ticker-badge--done' },
};

export default function TickerHeader({ game, project, round, meta, displayMinute, onClockAction, onMetaUpdate }) {
    const navigate = useNavigate();
    const [editingScore, setEditingScore] = useState(false);
    const [scoreInput, setScoreInput] = useState({ home: '', away: '' });

    const homeTeam  = game?.hid?.name || 'Heim';
    const awayTeam  = game?.aid?.name || 'Gast';
    const homeScore = meta?.scoreFT?.home ?? game?.scoreh;
    const awayScore = meta?.scoreFT?.away ?? game?.scorea;

    const status     = meta?.status || 'prepared';
    const statusInfo = STATUS[status] || STATUS.prepared;
    const isLive     = status === 'live';
    const isPaused   = meta?.clockPaused;
    const half       = meta?.currentHalf || 1;
    const minuteStr  = formatMinute(displayMinute, half);

    const openScoreEdit = () => {
        setScoreInput({
            home: homeScore !== null && homeScore !== undefined ? String(homeScore) : '0',
            away: awayScore !== null && awayScore !== undefined ? String(awayScore) : '0',
        });
        setEditingScore(true);
    };

    const saveScore = async () => {
        try {
            const scoreFT = {
                home: parseInt(scoreInput.home, 10) || 0,
                away: parseInt(scoreInput.away, 10) || 0,
            };
            const res = await matchMetaAPI.update(game._id, { scoreFT });
            onMetaUpdate?.(res.data);
        } catch (e) { console.error(e); }
        setEditingScore(false);
    };

    return (
        <header className="ticker-header">
            {/* Top bar: back + breadcrumb */}
            <div className="flex items-center justify-between px-4 pt-3 pb-1">
                <button
                    type="button"
                    onClick={() => navigate(`/schedule/${project?._id}/rounds/${round?._id}`)}
                    className="text-xs text-slate-500 hover:text-white transition-colors flex items-center gap-1"
                >
                    ← {round?.name || 'Spiele'}
                </button>
                <span className="text-xs text-slate-600 truncate max-w-[40%]">{project?.name}</span>
            </div>

            {/* Scoreboard row */}
            <div className="px-4 py-3 flex items-center gap-3 sm:gap-6">
                {/* Home */}
                <div className="flex-1 min-w-0 text-right">
                    <div className="text-sm sm:text-base font-bold truncate text-slate-100">{homeTeam}</div>
                </div>

                {/* Center: minute + score */}
                <div className="flex-none flex flex-col items-center gap-1">
                    {minuteStr && isLive && (
                        <div className="ticker-minute-display text-3xl sm:text-4xl font-black">
                            {minuteStr}
                            {isPaused && <span className="text-yellow-400 text-lg ml-1">⏸</span>}
                        </div>
                    )}

                    {editingScore ? (
                        <div className="flex items-center gap-1">
                            <input
                                type="number" min="0" max="99"
                                value={scoreInput.home}
                                onChange={e => setScoreInput(s => ({ ...s, home: e.target.value }))}
                                className="w-10 text-center font-black text-xl bg-slate-800 border border-slate-600 rounded text-white"
                                autoFocus
                                onKeyDown={e => { if (e.key === 'Enter') saveScore(); if (e.key === 'Escape') setEditingScore(false); }}
                            />
                            <span className="text-slate-500 font-black text-xl">:</span>
                            <input
                                type="number" min="0" max="99"
                                value={scoreInput.away}
                                onChange={e => setScoreInput(s => ({ ...s, away: e.target.value }))}
                                className="w-10 text-center font-black text-xl bg-slate-800 border border-slate-600 rounded text-white"
                                onKeyDown={e => { if (e.key === 'Enter') saveScore(); if (e.key === 'Escape') setEditingScore(false); }}
                            />
                            <button type="button" onClick={saveScore} className="ticker-clock-btn ticker-clock-btn--go ml-1">✓</button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={openScoreEdit}
                            className="text-3xl sm:text-4xl font-black font-mono tabular-nums text-white hover:text-green-400 transition-colors"
                            title="Spielstand bearbeiten"
                        >
                            {homeScore ?? '–'}<span className="text-slate-600 mx-1">:</span>{awayScore ?? '–'}
                        </button>
                    )}

                    <span className={`ticker-badge ${statusInfo.badge} ${isLive && !isPaused ? 'ticker-live-pulse' : ''}`}>
                        {isLive && !isPaused && <span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block" />}
                        {statusInfo.label}
                    </span>
                </div>

                {/* Away */}
                <div className="flex-1 min-w-0 text-left">
                    <div className="text-sm sm:text-base font-bold truncate text-slate-100">{awayTeam}</div>
                </div>
            </div>

            {/* Clock controls — compact pill row */}
            <div className="px-4 pb-3 flex flex-wrap items-center justify-center gap-2">
                {status === 'prepared' && (
                    <button type="button" className="ticker-clock-btn ticker-clock-btn--go" onClick={() => onClockAction('kickoff')}>
                        ▶ Anpfiff
                    </button>
                )}
                {isLive && !isPaused && (
                    <>
                        <button type="button" className="ticker-clock-btn ticker-clock-btn--pause" onClick={() => onClockAction('pause')}>⏸ Pause</button>
                        {half === 1 && <button type="button" className="ticker-clock-btn" onClick={() => onClockAction('halftime')}>Halbzeit</button>}
                        {half === 2 && <button type="button" className="ticker-clock-btn ticker-clock-btn--stop" onClick={() => onClockAction('fulltime')}>Abpfiff</button>}
                    </>
                )}
                {isLive && isPaused && (
                    <>
                        <button type="button" className="ticker-clock-btn ticker-clock-btn--go" onClick={() => onClockAction('resume')}>▶ Weiter</button>
                        {half === 1 && <button type="button" className="ticker-clock-btn" onClick={() => onClockAction('halftime')}>Halbzeit</button>}
                        <button type="button" className="ticker-clock-btn ticker-clock-btn--stop" onClick={() => onClockAction('fulltime')}>Abpfiff</button>
                    </>
                )}
                {status === 'halftime' && (
                    <>
                        <button type="button" className="ticker-clock-btn ticker-clock-btn--go" onClick={() => onClockAction('second_half')}>▶ 2. HZ</button>
                        <button type="button" className="ticker-clock-btn ticker-clock-btn--stop" onClick={() => onClockAction('fulltime')}>Abpfiff</button>
                    </>
                )}
                {status === 'finished' && (
                    <span className="text-xs text-slate-500">Spiel beendet</span>
                )}
            </div>
        </header>
    );
}
